import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { replace } from 'connected-react-router'
import { RouteComponentProps, withRouter } from 'react-router'
import HeadData from 'components/Common/HeadData'
import {
  deleteSearchParams, getCheckInDateFromURLSearchParams, getCheckOutDateFromURLSearchParams, getFlexibleMonthsFromURLSearchParams, getFlexibleNightsFromURLSearchParams, setManySearchParamValues, setSearchParamValue,
  parseSearchString,
  validateBoundsSearchParams,
  validateDestinationSearchParams,
  validateLandmarkSearchParams,
  validatePropertySearchParams,
} from 'lib/url/searchUrlUtils'
import { SearchFormRef } from 'components/Search'
import config from 'constants/config'
import { addGTMEvent } from 'api/googleTagManager'
import { searchOfferListFilters, searchResultDealClick, searchResultImpressionCount, searchSessionDealClick } from 'analytics/eventDefinitions'
import { decodeSearchParams, getSearchTypeFromFilters } from 'lib/search/searchUtils'
import {
  getPageTitle,
  saveRecentSearch,
  getAppliedFilterCount,
  FijiAlternativeDestination,
} from 'components/Search/utils'
import Heading from 'components/Luxkit/Typography/Heading'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import TextButton from 'components/Luxkit/Button/TextButton'
import OfferRecommendations from 'components/Common/OfferRecommendations'
import isAnyFilterApplied from 'lib/offer/filtersUtils'
import { queryKeyFilters } from 'constants/url'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import styled from 'styled-components'
import HotelSearchNoSearchResultSection from './HotelSearchNoSearchResultSection'
import LayoutContainer from 'components/Common/LayoutContainer'
import { useGlobalHotelsSearchContext, useGlobalHotelsSearchResultsTracking } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { GlobalSearchStateActions, GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import { DepositTagOffersProvider } from 'contexts/depositTagOffersContext'
import { replaceWithRegion } from 'actions/NavigationActions'
import OfferList, { OfferListV2Item } from 'components/OfferList/OfferList'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import HotelSearchNearbyPropertyOfferList from './HotelSearchNearbyPropertyOfferList'
import HotelSearchExpandedDestinationOfferList from './HotelSearchExpandedDestinationOfferList'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import useOfferList from 'hooks/Offers/useOfferList'
import { HOTEL_SEARCH_TYPES, OFFER_TYPE_BED_BANK, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_VILLA } from 'constants/offer'
import HotelSearchHeader from './HotelSearchHeader'
import { landmarkSearchSortByTypes, searchSortByTypes, SORT_OPTION_CUSTOMER_RATING, SORT_OPTION_INCLUSIONS, SORT_OPTION_NEWEST, SORT_OPTION_VALUE } from 'constants/offerListFilters'
import HotelSearchDestinationNoResults from './HotelSearchDestinationNoResults'
import useQueryParams from 'hooks/useQueryParams'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import Spacing from 'components/Common/Spacing'
import { rem } from 'polished'
import cn, { clsx } from 'clsx'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { SEARCH_VERTICALS, FLEXIBLE_DURATION_RANGE, ANYWHERE_NAME } from 'constants/search'
import { without } from 'lib/array/arrayUtils'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import BusinessTravellerStickyBudgetBar from 'businessTraveller/components/budget-bar/accommodation/BusinessTravellerStickyBudgetBar'
import { mediaQueryUp } from 'components/utils/breakpoint'
import HotelSearchPageFilterPane from './HotelSearchPageFilterPane'
import OfferCountAndSort from './OfferCountAndSort'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { useIsMobileScreen, useIsLargeDesktopScreen } from 'lib/web/deviceUtils'
import { getListName } from 'analytics/enhanced-ecommerce'
import { EnabledFilters } from 'components/Search/type'
import zIndex from 'styles/tools/z-index'
import { fetchPopularHotelFilters } from 'actions/OfferActions'
import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import HotelSearchLowResults from './HotelSearchLowResults'
import HotelSearchFlightsDeal from './HotelSearchFlightsDeal'
import { OptimizelyExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import * as Analytics from 'analytics/analytics'
import { isLuxuryPlusEnabled } from 'loyalty/selectors/featureToggle'
import useSlimMobileSearch from 'components/SearchV2/Hooks/useSlimMobileSearch'
import useSearchResultStatsTrack from 'hooks/Search/useSearchResultStatsTrack'
import { fetchSpecificPlaces } from 'actions/PlaceActions'
import { searchEvent } from 'analytics/snowplow/events'
import HotelSearchDestinationCarousel from 'components/Pages/HotelSearchPage/HotelSearchDestinationCarousel'
import useSearchFlashCount from 'hooks/Search/useSearhFlashCount'
import useSearchFlightDeal from 'components/SearchV2/Hooks/useSearchFlightDeal'

const destinationTypes = new Set(['destination'])

const ANALYTICS_HOTEL_SEARCH_TAG = 'HS'

const StyledBusinessTravellerStickyBudgetBar = styled(BusinessTravellerStickyBudgetBar)`
  z-index: ${zIndex.stickyHeaderBanner};
`

const DiscoverOtherOffers = styled(VerticalSpacer)`
  text-align: center;
`

const StyledSpacing = styled(Spacing)`
    background-color: ${props => props.theme.palette.neutral.default.eight};
  &.fullWidth {
    background-color: ${props => props.theme.palette.neutral.default.six};
  }
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.default.eight};
    display: grid;
    padding: ${rem(24)} 0;
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 1fr);
      padding-top: ${rem(12)};
    }
  }
  &.fullWidth {
    padding: 0;
  }
`

const StyledHotelSearchPageFilterPane = styled(HotelSearchPageFilterPane)`
  grid-area: offer-filters;
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
`

function mapState(state: App.State) {
  const filters = getUrlOfferListFilters(state)

  return {
    windowSearch: state.router.location.search,
    filters,
    memberId: state.auth.account.memberId,
    searchResultMetadata: state.offer.searchResultMetadata,
    isFlexibleSearch: !!(filters?.flexibleMonths && filters?.flexibleNights),
    travellerEmployees: selectSelectedTravellerEmployees(state),
    specificPlaces: state.destination.specificPlaces[filters.destinationId ?? ''],
  }
}

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

interface Props extends PropsFromRedux, RouteComponentProps<any> {}

function HotelSearchPage({
  filters,
  match,
  memberId,
  searchResultMetadata,
  windowSearch,
  isFlexibleSearch,
  location,
  history,
  travellerEmployees,
  specificPlaces,
}: Props) {
  const dispatch = useAppDispatch()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const isMobileScreen = useIsMobileScreen()
  const isDesktop = useIsLargeDesktopScreen()
  const searchType = getSearchTypeFromFilters(filters)
  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const searchParams = useMemo(() => decodeSearchParams(windowSearch), [windowSearch])
  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const showSlimMobile = useSlimMobileSearch()

  const {
    searchTargetLabel,
    offerDistanceFromSearchTarget,
  } = useMemo(() => ({
    searchTargetLabel: params.landmarkName || params.destinationName || params.propertyName,
    offerDistanceFromSearchTarget: searchResultMetadata.distanceFromSearchTarget[params.landmarkId || params.destinationId],
  }), [
    params.landmarkId,
    params.landmarkName,
    params.destinationId,
    params.destinationName,
    params.propertyName,
    searchResultMetadata.distanceFromSearchTarget,
  ])

  const luxPlusEnabled = useAppSelector(isLuxuryPlusEnabled)
  const enabledFilters: EnabledFilters = useMemo(() => (
    {
      inclusions: true,
      amenities: true,
      customerRating: true,
      priceFilter: config.ENABLE_HOTEL_SEARCH_PAGE_PRICE_FILTER,
      holidayTypes: searchTargetLabel !== ANYWHERE_NAME,
      locations: searchTargetLabel == ANYWHERE_NAME,
      offerTypes: config.ENABLE_VILLA_RENTALS,
      popularFilters: true,
      luxPlusFeatures: luxPlusEnabled,
    }
  ), [searchTargetLabel, luxPlusEnabled])

  useEffect(() => {
    if (searchParams?.searchItem?.searchType === 'destination') {
      dispatch(fetchSpecificPlaces(searchParams.searchItem.value))
    }
  }, [dispatch, searchParams.searchItem])

  useEffect(() => {
    const { searchItem, dates: specificDates, rooms, flexibleSearch } = searchParams
    const shouldUseMemberId = !config.ENABLE_LOGGED_OUT_RECENT_SEARCH
    if ((memberId || !shouldUseMemberId) && searchItem && (destinationTypes.has(searchType) || searchType === 'property')) {
      const dates = {
        checkIn: specificDates?.checkIn,
        checkOut: specificDates?.checkOut,
        flexibleMonths: flexibleSearch?.flexibleMonths,
        flexibleDuration: flexibleSearch?.flexibleNights,
        isAnytime: !(specificDates?.checkIn || specificDates?.checkOut || flexibleSearch?.flexibleMonths || flexibleSearch?.flexibleNights),
      }
      saveRecentSearch(searchItem, dates, rooms, memberId ?? '', travellerEmployees)
    }
  }, [searchParams, memberId, searchType, travellerEmployees])

  const resetFilters = () => {
    const search = deleteSearchParams(
      windowSearch,
      ...queryKeyFilters,
    )
    dispatch(replace({ search }))
    scrollTo(0, 0)
  }

  const formRef = useRef<SearchFormRef>(null)

  const onBedbankSelectDatesPrompt = useCallback((offerId: string, offerType: App.OfferType, windowSearch: string) => {
    const paramsToSet = [{ paramName: 'offerType', paramValue: offerType }, { paramName: 'offerId', paramValue: offerId }]
    if (isMobileScreen) {
      toggleSearchVertical(SEARCH_VERTICALS.HOTELS, { withDateSelect: true, search: setManySearchParamValues(windowSearch, paramsToSet) })
    } else {
      history.replace({ search: setManySearchParamValues(windowSearch, paramsToSet) })
      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [history, isMobileScreen, toggleSearchVertical])
  const queryParams = useQueryParams()
  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    searchTargetLabel,
    offerDistanceFromSearchTarget,
    onBedbankSelectDatesPrompt,
    eventAnalytics: {
      contextLocation: 'search-list',
    },
  }, queryParams)

  const pageTitle = useMemo(() => {
    const { searchItem } = searchParams
    const checkInDate = getCheckInDateFromURLSearchParams(queryParams)
    const checkOutDate = getCheckOutDateFromURLSearchParams(queryParams)
    const flexibleMonths = getFlexibleMonthsFromURLSearchParams(queryParams)
    const flexibleNights = getFlexibleNightsFromURLSearchParams(queryParams)

    return getPageTitle(searchItem, { checkIn: checkInDate, checkOut: checkOutDate, flexibleMonths, flexibleNights: flexibleNights as FLEXIBLE_DURATION_RANGE })
  }, [queryParams, searchParams])

  const searchedDates = useMemo(() => {
    return {
      checkInDate: getCheckInDateFromURLSearchParams(queryParams),
      checkOutDate: getCheckOutDateFromURLSearchParams(queryParams),
    }
  }, [queryParams])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_OFFER_DISTANCE_FROM_SEARCH_TARGET,
      offerDistanceFromSearchTarget,
    })
  }, [offerDistanceFromSearchTarget, globalHotelsSearchDispatch])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_TARGET_LABEL,
      searchTargetLabel,
    })
  }, [searchTargetLabel, globalHotelsSearchDispatch])

  useIdleTimeReporter(IdleTimeSource.HotelSearchPage)

  const invalidSearchParams = useMemo(() =>
    !validateDestinationSearchParams(params) &&
    !validateLandmarkSearchParams(params) &&
    !validatePropertySearchParams(params) &&
    !validateBoundsSearchParams(params),
  [params])

  useEffect(() => {
    const onSearchUrlPath = match.url === location.pathname
    if (invalidSearchParams && onSearchUrlPath) {
      dispatch(replaceWithRegion('/hotels'))
    }
  }, [dispatch, invalidSearchParams, location.pathname, match])

  useEffect(() => {
    dispatch(fetchPopularHotelFilters())
  }, [dispatch])

  const nextAlgorithm = !!useOptimizelyExperiment(OptimizelyExperiments.nonFlashNextAlgo)

  const listFilters = useMemo((): App.OfferListFilters => {
    let offerTypes: Array<App.OfferListOfferType> = [...HOTEL_SEARCH_TYPES, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_BED_BANK]
    if (config.ENABLE_VILLA_RENTALS) {
      offerTypes.push(OFFER_TYPE_VILLA)
    }
    if (filters.offerTypes?.includes('rental')) {
      offerTypes = filters.offerTypes
    } else if (filters.offerTypes?.includes('hotel')) {
      offerTypes = without(offerTypes, OFFER_TYPE_VILLA)
    }

    const distanceEq = searchType === 'destination' ? 0 : undefined
    const distanceLte = searchType === 'landmark' || searchType === 'airport' ? 3000 : undefined

    return {
      ...filters,
      offerTypes,
      distanceEq,
      distanceLte,
      includeSpecificDatesResults: true,
      showUnavailableResult: true,
      applyNextEvAlgorithm: nextAlgorithm,
      scheduleVisibilities: isSpoofed ? ['listVisible', 'spoofVisible'] : ['listVisible'],
    }
  }, [filters, searchType, nextAlgorithm, isSpoofed])

  const filterCount = useMemo(() => {
    return getAppliedFilterCount(listFilters, enabledFilters)
  }, [listFilters, enabledFilters])

  const hasFiltersApplied = filterCount > 0

  const nearbyPropertyListFilters = useMemo((): App.OfferListFilters => {
    return {
      ...listFilters,
      searchNearby: true,
    }
  }, [listFilters])

  const hotelOfferList = useOfferList(listFilters, { clientCheckAvailability: true })
  const searchListKey = hotelOfferList.key
  useSearchResultStatsTrack(searchListKey)
  const flashCount = useSearchFlashCount(searchListKey)

  const expandedHotelList = useOfferList({
    ...listFilters,
    distanceGt: 0,
    distanceEq: undefined,
  }, { disabled: searchType === 'property' })

  const allHotelLists = useMemo(() => [hotelOfferList], [hotelOfferList])

  const nearbyOfferList = useOfferList(nearbyPropertyListFilters, { disabled: searchType !== 'property' })
  const allNearbyPropertyLists = useMemo(() => [nearbyOfferList], [nearbyOfferList])

  const searchSessionId = useAppSelector(state => state.analytics.search.sessions[searchListKey])

  useGlobalHotelsSearchResultsTracking(
    globalHotelsSearchState,
    allHotelLists,
    searchListKey,
    searchType === 'property' ? allNearbyPropertyLists : [expandedHotelList],
  )

  const listDepth = useRef<number>(0)
  useEffect(() => {
    return () => {
      if (hotelOfferList.offerIds.length) {
        // On a new search, finalise tracking the depth reached on the old search
        addGTMEvent(searchResultImpressionCount(hotelOfferList.offerIds.length, listDepth.current))
        // event sent, now reset the depth we're at. They probably applied a filter and will be at the top again
        listDepth.current = 0
      }
    }
    // eslint-disable-next-line
  }, [searchListKey])

  useEffect(() => {
    if (listFilters) {
      addGTMEvent(searchOfferListFilters(listFilters))
    }
  }, [listFilters])

  const tracking = useOfferListTracking(
    getListName('OfferList', ANALYTICS_HOTEL_SEARCH_TAG, 'Search Page Offer List'),
    {
      ias: {
        list: 'hotels-search',
        listId: searchSessionId,
      },
    })

  const onListEvent = useCallback((action: OfferListEvents, data: any) => {
    // we only want to fire this event on the main offer list
    // and not the extra offer list to avoid dup events
    if (action === OfferListEvents.listLoad && searchListKey === data.key) {
      Analytics.trackEvent(searchEvent('offer-list-load', 'hotels-search-page', {
        filters,
        searchItem: searchParams.searchItem,
        checkIn: filters.checkIn,
        checkOut: filters.checkOut,
        vertical: SEARCH_VERTICALS.HOTELS,
        occupancies: filters.rooms,
      }))
    }

    if (action === OfferListEvents.productClick) {
      const { offer } = data
      addGTMEvent(searchResultDealClick())
      addGTMEvent(searchSessionDealClick(searchSessionId, offer.id))
      Analytics.trackClientEvent({
        subject: 'search-click-position',
        action: `ab-value: ${data.index + 1}`,
        category: 'logging',
        type: 'operational',
        optimizelyEventId: '29214570602',
        optimizelyEventKey: 'search-click-position',
      })
    } else if (action === OfferListEvents.impression) {
      listDepth.current = Math.max(data.index + 1, listDepth.current)
      Analytics.trackClientEvent({
        subject: 'search-offer',
        action: 'impression',
        category: 'logging',
        type: 'operational',
        optimizelyEventId: '29308260023',
        optimizelyEventKey: 'search-offer-impression',
      })
    }
  }, [searchSessionId, searchParams.searchItem, filters, searchListKey])

  const onPageChange = useCallback((page: number) => {
    dispatch(replace({ search: setSearchParamValue(windowSearch, 'pages', page) }))
    // eslint-disable-next-line
  }, [windowSearch])

  const sortOptions = useMemo(() => {
    let options = searchType === 'landmark' ? landmarkSearchSortByTypes : searchSortByTypes

    options = [...options, SORT_OPTION_CUSTOMER_RATING]

    if (config.businessTraveller.currentAccountMode === 'business') {
      options = options.filter((option) => option !== SORT_OPTION_VALUE && option !== SORT_OPTION_NEWEST && option !== SORT_OPTION_INCLUSIONS)
    }

    return options
  }, [searchType])

  const showFijiSearchCarousel: boolean = !!useOptimizelyExperiment(OptimizelyExperiments.SearchFijiExpansion, params.destinationName === 'Fiji')

  // TODO: this is for a future LERE test
  // START ---------  lere nearby high value offer recos in the search result ----------
  // const { searchItem, rooms, dates } = searchParams
  // const placeId = searchItem?.value ?? ''
  // const enableLereNearbyOffers = destinationTypes.has(searchType) && !!placeId && currentRegionCode === 'AU'
  // const lereNearbyOfferIds = useLereNearbyOffers(enableLereNearbyOffers, placeId, rooms, dates.checkIn, dates.checkOut)

  // const lereNearbyOfferList: OfferListV2Item = useMemo(() => {
  //   const candidateIds = without(lereNearbyOfferIds, ...hotelOfferList.offerIds)
  //   if (candidateIds.length > 0) {
  //     const lereSet = new Set(lereNearbyOfferIds)
  //     // find first offer in search result where it is not in lere recos (low value)
  //     // since lere recos are ranked by distance, it should contain all high value offers from search polygon
  //     // so if a search result is not in lere reco (and there are recos from lere not in serach result)
  //     // such search result must be low value
  //     const firstLowValuePosition = hotelOfferList.offerIds.findIndex(id => !lereSet.has(id))

  //     const insertPosition = firstLowValuePosition === -1 ? 'end' : Math.max(firstLowValuePosition, 5)
  //     const lereOfferList: App.OfferList = {
  //       offerIds: candidateIds,
  //       key: 'lere-nearby-high-value-offers',
  //       fetching: false,
  //     }
  //     return {
  //       id: 'lere-nearby-offers',
  //       position: insertPosition,
  //       element: <OfferRecommendations useList trackingDescription="Search Results - high mpv offers nearby" overrideOfferList={lereOfferList} maxOffers={MAX_LERE_NEARBY_OFFERS}/>,
  //       show: destinationTypes.has(searchType),
  //     }
  //   }
  //   return {
  //     id: 'lere-nearby-offers',
  //     position: 'end',
  //     element: null,
  //     show: false,
  //   }
  // }, [lereNearbyOfferIds, hotelOfferList.offerIds, searchType])

  // END ---------  lere nearby high value offer recos in the search result ----------

  const [flightDeals] = useFlightDeals()

  const flightDeal = useSearchFlightDeal(
    flightDeals,
    {
      checkInDate: getCheckInDateFromURLSearchParams(queryParams),
      flexibleMonths: getFlexibleMonthsFromURLSearchParams(queryParams),
    },
    params.destinationName === 'Bali' ? 'DPS' : undefined,
  )

  const flightDealLevel: '0' | '4' | undefined = useOptimizelyExperiment(
    OptimizelyExperiments.searchFlightDeal,
    ((searchTargetLabel === ANYWHERE_NAME || params.destinationName === 'Bali') && !!flightDeal),
  )

  if (invalidSearchParams) {
    return null
  }

  const mainListTotal = searchType === 'property' ||
    (!hotelOfferList.offerCount || filters.limit) ?
    hotelOfferList.offerIds.length : hotelOfferList.offerCount

  const mainListFetching = hotelOfferList.fetching
  const destinationNameForHtml = params.destinationName ? decodeURIComponent(params.destinationName).replace(/\+/g, ' ') : ''

  const showFilterPane = isDesktop && (mainListTotal > 0 || mainListFetching || hasFiltersApplied)

  const offerListFiltersProps = {
    sortOptions,
    searchType,
    switchView: {
      label: 'Map view',
      view: 'map' as const,
      URL: mapViewURL,
    },
    listFilters,
  }
  const extras: Array<OfferListV2Item> = [{
    id: 'no-results',
    position: 'start',
    element: <HotelSearchNoSearchResultSection searchType={searchType} />,
    show: !destinationTypes.has(searchType) && mainListTotal === 0 && !mainListFetching,
  },
  {
    id: 'no-results-dest',
    position: 'start',
    element: <HotelSearchDestinationNoResults location={destinationNameForHtml} filters={listFilters} />,
    show: destinationTypes.has(searchType) && mainListTotal === 0 && !mainListFetching,
  },
  {
    id: 'heading',
    position: 'start',
    element: <OfferCountAndSort
      destinationName={params.destinationName}
      specificPlaces={specificPlaces}
      fetching={mainListFetching}
      mainListTotal={mainListTotal}
      sortOptions={sortOptions}
      mapViewUrl={mapViewURL}
      isFlexible={isFlexibleSearch}
      filters={listFilters}
      windowSearch={windowSearch}
      shouldShowFilters={!showFilterPane}
      showHolidayTypes={searchTargetLabel !== ANYWHERE_NAME}
      searchType={searchType}
      enabledFilters={enabledFilters}
    />,
  },
  {
    id: 'low-results',
    position: 'start',
    element: <HotelSearchLowResults filters={listFilters} numResults={mainListTotal} />,
    show: mainListTotal > 0 && mainListTotal < 5 && !mainListFetching,
  },
  {
    id: 'fiji-search-carousel',
    position: (params.sortBy || (flashCount < 5)) ? 5 : flashCount,
    element: <HotelSearchDestinationCarousel destinations={FijiAlternativeDestination} filters={listFilters}/>,
    show: showFijiSearchCarousel,
  },
  // TODO: this is for a future LERE test
  // lereNearbyOfferList,

  // This includes nearby offer and top exclusive offer on zero result page
  {
    id: 'destination-expanded-results',
    position: 'end',
    element: <HotelSearchExpandedDestinationOfferList filters={listFilters} enableLere={destinationTypes.has(searchType)} />,
    show: destinationTypes.has(searchType),
  },
  {
    id: 'discover-other-offers',
    position: 'end',
    show: !!(params.destinationName && isAnyFilterApplied(params)) && config.businessTraveller.currentAccountMode !== 'business',
    element: <DiscoverOtherOffers gap={16}>
      <Heading variant="heading4">Discover all offers in</Heading>
      <TextButton
        kind="primary"
        variant="ghost"
        startIcon={<LineSearchIcon />}
        onClick={resetFilters}
        size="large"
      >
        {params.destinationName}
      </TextButton>
    </DiscoverOtherOffers>,
  },
  {
    id: 'nearby-property-offers',
    position: 'end',
    element: <HotelSearchNearbyPropertyOfferList filters={listFilters} />,
    show: searchType === 'property',
  }, {
    id: 'flight-deals',
    position: flightDealLevel ? parseInt(flightDealLevel) : 'start',
    element: flightDeal ? <HotelSearchFlightsDeal flightDeal={flightDeal} /> : null,
    show: !!flightDealLevel,
  }, {
    id: 'recommendations',
    position: 'end',
    element: <OfferRecommendations useList trackingDescription="Search Results" />,
    show: searchType === 'property' && config.businessTraveller.currentAccountMode !== 'business',
  }]

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
      <GlobalSearchContextProvider
        state={globalHotelsSearchState}
        dispatch={globalHotelsSearchDispatch}
      >
        <HeadData noIndex title={pageTitle} />
        <HotelSearchHeader
          filters={filters}
          formRef={formRef}
          offerListFiltersProps={offerListFiltersProps}
          searchVertical={SEARCH_VERTICALS.HOTELS}
          baseSearchPath="/search"
          baseSearchMapPath="/search/map"
        />
        <ErrorBoundary fallback={null}>
          <PromptCardSelector tripReengagement />
        </ErrorBoundary>
        {config.businessTraveller.currentAccountMode === 'business' && <StyledBusinessTravellerStickyBudgetBar
          checkInDate={searchedDates.checkInDate}
          checkOutDate={searchedDates.checkOutDate}
        />}
        <StyledSpacing pt={showSlimMobile ? 16 : 24} ptSm={32} className={clsx({ fullWidth: showSlimMobile })}>
          <StyledLayoutContainer size={showFilterPane ? 'xl' : 'lg'} className={cn({ filterPane: showFilterPane, fullWidth: showSlimMobile })}>
            <StyledVerticalSpacer gap={16}>
              <DepositTagOffersProvider>
                <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
                  <OfferList
                    filters={listFilters}
                    interstitials={extras}
                    onPageChange={onPageChange}
                    tileStyle="search"
                    eagerLoadFirstImage
                    defaultPage={params.pages ? parseInt(params.pages) : 1}
                    fullWidth={showSlimMobile}
                    clientCheckAvailability
                  />
                </OfferListEventsProvider>
              </DepositTagOffersProvider>
            </StyledVerticalSpacer>
            {(config.businessTraveller.currentAccountMode === 'business' || showFilterPane) && <StyledHotelSearchPageFilterPane
              filters={listFilters}
              mapViewURL={mapViewURL}
              search={windowSearch}
              enabledFilters={enabledFilters}
              hotelOfferList={hotelOfferList}
            />}
          </StyledLayoutContainer>
        </StyledSpacing>
      </GlobalSearchContextProvider>
    </AnalyticsPageContext.Provider>
  )
}

export default withRouter(connector(HotelSearchPage))
